import { useQuery } from '@apollo/client/react';
import { Box, Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { JsonReadonlyBlock } from '#src/components/json-readonly-block';
import { GET_PROCESSOR_INSTANCE } from '#src/graphql/queries/processor-instance';

export function ProcessorInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const instanceId = id ?? '';

  const { data, loading, error, refetch } = useQuery(GET_PROCESSOR_INSTANCE, {
    variables: { id: instanceId },
    skip: !instanceId,
  });

  if (!instanceId) {
    return (
      <Box p={4}>
        <Text color="fg.muted">Missing instance id.</Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4}>
        <Spinner />
        <Text mt={2}>Loading processor instance…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => void refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  const row = data?.processorInstance;
  if (!row) {
    return (
      <Box p={4}>
        <Heading size="md" mb={2}>
          Not found
        </Heading>
        <Text color="fg.muted">
          No processor instance for id{' '}
          <Text as="span" fontFamily="mono">
            {instanceId}
          </Text>
          .
        </Text>
      </Box>
    );
  }

  return (
    <Box p={4} flex={1} minH={0} display="flex" flexDirection="column">
      <VStack gap={4} align="stretch">
        <Box flexShrink={0}>
          <Heading size="lg" color="fg">
            Processor instance
          </Heading>
          <VStack gap={1} align="stretch" mt={3}>
            <Field label="id" value={row.id} mono />
            <Field label="processorId" value={row.processorId} mono />
            <Field label="userId" value={row.userId} mono />
            <Field
              label="isActive"
              value={row.isActive ? 'true' : 'false'}
              mono
            />
            <Field
              label="createdAt"
              value={new Date(row.createdAt).toLocaleString()}
            />
            <Field
              label="updatedAt"
              value={new Date(row.updatedAt).toLocaleString()}
            />
            <Box>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                error
              </Text>
              {row.error != null && row.error !== '' ? (
                <Box
                  as="pre"
                  fontSize="sm"
                  fontFamily="mono"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  mt={1}
                >
                  {row.error}
                </Box>
              ) : (
                <Text fontSize="sm" color="fg.muted" mt={1}>
                  —
                </Text>
              )}
            </Box>
          </VStack>
        </Box>
        <Box flexShrink={0}>
          {row.value != null ? (
            <JsonReadonlyBlock
              title="value"
              value={row.value}
              fitContentHeight
            />
          ) : (
            <>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={1}>
                value
              </Text>
              <Text fontSize="sm" color="fg.muted">
                —
              </Text>
            </>
          )}
        </Box>
      </VStack>
    </Box>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" fontWeight="medium">
        {label}
      </Text>
      <Text
        fontSize="sm"
        color="fg"
        fontFamily={mono ? 'mono' : undefined}
        mt={0.5}
      >
        {value}
      </Text>
    </Box>
  );
}
